import { supabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';

type SystemLogRow = Database['public']['Tables']['system_logs']['Row'];
type SystemLogInsert = Database['public']['Tables']['system_logs']['Insert'];

// System Log Service for comprehensive activity logging using Supabase
export interface SystemLogEntry {
  id?: string;
  timestamp?: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'SECURITY';
  category: string;
  action: string;
  userId?: string;
  userName?: string;
  userRole?: string;
  ipAddress?: string;
  userAgent?: string;
  resource?: string;
  resourceId?: string;
  details: string;
  metadata?: { [key: string]: any };
  sessionId?: string;
  success: boolean;
  errorCode?: string;
  duration?: number;
}

// Add a log entry to Supabase
export const addSystemLog = async (logData: Omit<SystemLogEntry, 'id' | 'timestamp'>): Promise<void> => {
  try {
    const logEntry: any = {
      level: logData.level,
      category: logData.category,
      action: logData.action,
      user_id: logData.userId || null,
      user_name: logData.userName || null,
      user_role: logData.userRole || null,
      ip_address: logData.ipAddress || null,
      user_agent: logData.userAgent || null,
      resource: logData.resource || null,
      resource_id: logData.resourceId || null,
      details: logData.details,
      metadata: logData.metadata || null,
      session_id: logData.sessionId || null,
      success: logData.success,
      error_code: logData.errorCode || null,
      duration: logData.duration || null,
    };

    const { error } = await supabase
      .from('system_logs')
      .insert(logEntry);

    if (error) {
      console.error('Error adding system log:', error);
    }
  } catch (error) {
    console.error('Failed to add system log:', error);
  }
};

// Get all system logs from Supabase
export const getSystemLogs = async (): Promise<SystemLogEntry[]> => {
  try {
    const { data, error } = await supabase
      .from('system_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1000);

    if (error) {
      console.error('Error fetching system logs:', error);
      return [];
    }

    // Map database rows to SystemLogEntry format
    return (data || []).map((row: any) => ({
      id: row.id,
      timestamp: row.created_at,
      level: row.level as 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS' | 'SECURITY',
      category: row.category,
      action: row.action,
      userId: row.user_id || undefined,
      userName: row.user_name || undefined,
      userRole: row.user_role || undefined,
      ipAddress: row.ip_address || undefined,
      userAgent: row.user_agent || undefined,
      resource: row.resource || undefined,
      resourceId: row.resource_id || undefined,
      details: row.details,
      metadata: row.metadata || undefined,
      sessionId: row.session_id || undefined,
      success: row.success,
      errorCode: row.error_code || undefined,
      duration: row.duration || undefined,
    }));
  } catch (error) {
    console.error('Failed to fetch system logs:', error);
    return [];
  }
};

// Clear all logs (for testing purposes)
export const clearSystemLogs = async (): Promise<void> => {
  try {
    const { error } = await supabase
      .from('system_logs')
      .delete()
      .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all rows

    if (error) {
      console.error('Error clearing system logs:', error);
    }
  } catch (error) {
    console.error('Failed to clear system logs:', error);
  }
};

// Get client IP (simplified - in real app would use server-side detection)
const getClientIP = (): string => {
  // In a real application, this would be determined server-side
  // For demo purposes, using a placeholder
  return '192.168.1.' + Math.floor(Math.random() * 254 + 1);
};

// ===== AUTHENTICATION LOGGING =====

// Log successful login
export const logSuccessfulLogin = (user: { uid: string; fullName: string; roles: string[] }): void => {
  addSystemLog({
    level: 'SUCCESS',
    category: 'Authentication',
    action: 'User Login',
    userId: user.uid,
    userName: user.fullName,
    userRole: user.roles.join(', '),
    ipAddress: getClientIP(),
    userAgent: navigator.userAgent,
    details: `User ${user.fullName} successfully logged in`,
    success: true,
    duration: 1200,
  });
};

// Log failed login attempt
export const logFailedLogin = (username: string, reason: string = 'Invalid credentials'): void => {
  addSystemLog({
    level: 'SECURITY',
    category: 'Authentication',
    action: 'Failed Login Attempt',
    ipAddress: getClientIP(),
    userAgent: navigator.userAgent,
    details: `Failed login attempt for username: ${username}. Reason: ${reason}`,
    success: false,
    errorCode: 'AUTH_FAILED',
    metadata: {
      attemptedUsername: username,
      failureReason: reason,
    },
  });
};

// Log user logout
export const logUserLogout = (user: { uid: string; fullName: string }): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Authentication',
    action: 'User Logout',
    userId: user.uid,
    userName: user.fullName,
    ipAddress: getClientIP(),
    userAgent: navigator.userAgent,
    details: `User ${user.fullName} logged out`,
    success: true,
    duration: 500,
  });
};

// ===== REQUEST MANAGEMENT LOGGING =====

// Log TIL request submission
export const logTILRequestSubmission = (user: { uid: string; fullName: string }, requestData: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'TIL Request',
    action: 'TIL Request Submitted',
    userId: user.uid,
    userName: user.fullName,
    resource: 'TIL Request',
    resourceId: requestData.id || 'pending',
    details: `TIL request submitted for ${requestData.hours || 'N/A'} hours from ${requestData.fromDate} to ${requestData.toDate}`,
    success: true,
    metadata: {
      requestType: 'TIL',
      fromDate: requestData.fromDate,
      toDate: requestData.toDate,
      hours: requestData.hours,
      reason: requestData.reason
    }
  });
};

// Log Extra Hours request submission
export const logExtraHoursRequestSubmission = (user: { uid: string; fullName: string }, requestData: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Extra Hours Request',
    action: 'Extra Hours Request Submitted',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Extra Hours Request',
    resourceId: requestData.id || 'pending',
    details: `Extra hours request submitted for ${requestData.hours || 'N/A'} hours on ${requestData.date}`,
    success: true,
    metadata: {
      requestType: 'Extra Hours',
      date: requestData.date,
      hours: requestData.hours,
      reason: requestData.reason
    }
  });
};

// Log request approval
export const logRequestApproval = (user: { uid: string; fullName: string }, requestInfo: { id: string; requesterName: string }, requestType: string): void => {
  addSystemLog({
    level: 'SUCCESS',
    category: `${requestType} Request`,
    action: 'Request Approved',
    userId: user.uid,
    userName: user.fullName,
    resource: `${requestType} Request`,
    resourceId: requestInfo.id,
    details: `${requestType} request from ${requestInfo.requesterName} approved by ${user.fullName}`,
    success: true,
    metadata: {
      requestType,
      requestId: requestInfo.id,
      requesterName: requestInfo.requesterName,
      approverId: user.uid,
      approverName: user.fullName
    }
  });
};

// Log request rejection
export const logRequestRejection = (user: { uid: string; fullName: string }, requestInfo: { id: string; requesterName: string }, requestType: string, reason: string): void => {
  addSystemLog({
    level: 'WARNING',
    category: `${requestType} Request`,
    action: 'Request Rejected',
    userId: user.uid,
    userName: user.fullName,
    resource: `${requestType} Request`,
    resourceId: requestInfo.id,
    details: `${requestType} request from ${requestInfo.requesterName} rejected by ${user.fullName}. Reason: ${reason}`,
    success: true,
    metadata: {
      requestType,
      requestId: requestInfo.id,
      requesterName: requestInfo.requesterName,
      rejectedBy: user.uid,
      rejectorName: user.fullName,
      rejectionReason: reason
    }
  });
};

// ===== USER MANAGEMENT LOGGING =====

// Log user creation
export const logUserCreation = (admin: { uid: string; fullName: string }, newUserData: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'User Management',
    action: 'User Created',
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User',
    resourceId: newUserData.uid || 'pending',
    details: `New user ${newUserData.fullName} created by ${admin.fullName}`,
    success: true,
    metadata: {
      newUserName: newUserData.fullName,
      newUserEmail: newUserData.email,
      newUserRoles: newUserData.roles,
      createdBy: admin.uid
    }
  });
};

// Log user update
export const logUserUpdate = (admin: { uid: string; fullName: string }, updatedUserData: any, changes: string[]): void => {
  addSystemLog({
    level: 'INFO',
    category: 'User Management',
    action: 'User Updated',
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User',
    resourceId: updatedUserData.uid,
    details: `User ${updatedUserData.fullName} updated by ${admin.fullName}. Changes: ${changes.join(', ')}`,
    success: true,
    metadata: {
      targetUserName: updatedUserData.fullName,
      targetUserEmail: updatedUserData.email,
      changes,
      updatedBy: admin.uid
    }
  });
};

// Log user deletion
export const logUserDeletion = (admin: { uid: string; fullName: string }, deletedUser: { uid: string; fullName: string }): void => {
  addSystemLog({
    level: 'WARNING',
    category: 'User Management',
    action: 'User Deleted',
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User',
    resourceId: deletedUser.uid,
    details: `User ${deletedUser.fullName} deleted by ${admin.fullName}`,
    success: true,
    metadata: {
      deletedUserName: deletedUser.fullName,
      deletedBy: admin.uid,
      deleterName: admin.fullName
    }
  });
};

// Log user management actions
export const logUserManagementAction = (admin: { uid: string; fullName: string }, action: string, targetUser: any, details: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'User Management',
    action: action,
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User Account',
    resourceId: targetUser.uid || targetUser.id,
    details: details,
    success: true,
    metadata: {
      actionType: action,
      targetUserId: targetUser.uid || targetUser.id,
      targetUserName: targetUser.fullName || targetUser.full_name || targetUser.name,
      adminUser: admin.fullName
    }
  });
};

// Log role assignments
export const logRoleAssignment = (admin: { uid: string; fullName: string }, targetUser: any, roles: string[], action: 'assigned' | 'removed'): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Role Management',
    action: `Roles ${action}`,
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User Roles',
    resourceId: targetUser.uid || targetUser.id,
    details: `Roles ${action} for ${targetUser.fullName || targetUser.full_name || targetUser.name}: ${roles.join(', ')}`,
    success: true,
    metadata: {
      targetUserId: targetUser.uid || targetUser.id,
      targetUserName: targetUser.fullName || targetUser.full_name || targetUser.name,
      roles,
      action,
      assignedBy: admin.fullName
    }
  });
};

// Log department assignments
export const logDepartmentAssignment = (admin: { uid: string; fullName: string }, targetUser: any, department: string, action: 'assigned' | 'removed'): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Department Management',
    action: `Department ${action}`,
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User Department',
    resourceId: targetUser.uid || targetUser.id,
    details: `Department ${action} for ${targetUser.fullName || targetUser.full_name || targetUser.name}: ${department}`,
    success: true,
    metadata: {
      targetUserId: targetUser.uid || targetUser.id,
      targetUserName: targetUser.fullName || targetUser.full_name || targetUser.name,
      department,
      action,
      assignedBy: admin.fullName
    }
  });
};

// Log balance adjustments
export const logBalanceAdjustment = (admin: { uid: string; fullName: string }, targetUser: any, balanceType: string, oldBalance: number, newBalance: number, reason: string): void => {
  const adjustment = newBalance - oldBalance;
  addSystemLog({
    level: 'INFO',
    category: 'Balance Management',
    action: 'Balance Adjusted',
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'User Balance',
    resourceId: targetUser.uid || targetUser.id,
    details: `${balanceType} balance adjusted for ${targetUser.fullName || targetUser.full_name || targetUser.name}: ${adjustment > 0 ? '+' : ''}${adjustment} minutes. Reason: ${reason}`,
    success: true,
    metadata: {
      targetUserId: targetUser.uid || targetUser.id,
      targetUserName: targetUser.fullName || targetUser.full_name || targetUser.name,
      balanceType,
      oldBalance,
      newBalance,
      adjustment,
      reason,
      adjustedBy: admin.fullName
    }
  });
};

// ===== NAVIGATION AND SYSTEM ACTIVITY LOGGING =====

// Log navigation events
export const logNavigation = (user: { uid: string; fullName: string }, fromPage: string, toPage: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Navigation',
    action: 'Page Navigation',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Page',
    resourceId: toPage,
    details: `User navigated from ${fromPage} to ${toPage}`,
    success: true,
    metadata: {
      fromPage,
      toPage,
      navigationTimestamp: new Date().toISOString()
    }
  });
};

// ===== DATA AND CALENDAR LOGGING =====

// Log data export events
export const logDataExport = (user: { uid: string; fullName: string }, exportType: string, filters?: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Data Export',
    action: 'Data Exported',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Export',
    resourceId: exportType,
    details: `${exportType} data exported by ${user.fullName}`,
    success: true,
    metadata: {
      exportType,
      filters,
      exportTimestamp: new Date().toISOString()
    }
  });
};

// Log calendar events
export const logCalendarEvent = (user: { uid: string; fullName: string }, action: string, eventDetails: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Calendar',
    action: action,
    userId: user.uid,
    userName: user.fullName,
    resource: 'Calendar Event',
    resourceId: eventDetails.id || 'unknown',
    details: `Calendar ${action.toLowerCase()}: ${eventDetails.title || 'Unknown Event'}`,
    success: true,
    metadata: {
      eventDetails,
      calendarAction: action
    }
  });
};

// ===== FORM AND UI INTERACTIONS =====

// Log form submissions
export const logFormSubmission = (user: { uid: string; fullName: string }, formName: string, formData: any, success: boolean = true): void => {
  addSystemLog({
    level: success ? 'SUCCESS' : 'WARNING',
    category: 'Form Submission',
    action: 'Form Submitted',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Form',
    resourceId: formName,
    details: `User submitted ${formName} form ${success ? 'successfully' : 'with errors'}`,
    success: success,
    metadata: {
      formName,
      formData,
      submissionTimestamp: new Date().toISOString()
    }
  });
};

// Log dashboard card clicks
export const logDashboardCardClick = (user: { uid: string; fullName: string }, cardName: string, cardType: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Dashboard Interaction',
    action: 'Dashboard Card Clicked',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Dashboard Card',
    resourceId: cardName,
    details: `User clicked on ${cardName} ${cardType} card`,
    success: true,
    metadata: {
      cardName,
      cardType,
      clickTimestamp: new Date().toISOString()
    }
  });
};

// Log menu navigation
export const logMenuNavigation = (user: { uid: string; fullName: string }, menuItem: string, destination: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Menu Navigation',
    action: 'Menu Item Clicked',
    userId: user.uid,
    userName: user.fullName,
    resource: 'Menu Item',
    resourceId: menuItem,
    details: `User navigated to ${destination} via ${menuItem} menu`,
    success: true,
    metadata: {
      menuItem,
      destination,
      navigationTimestamp: new Date().toISOString()
    }
  });
};

// ===== SYSTEM SETTINGS LOGGING =====

// Log system settings change
export const logSettingsChange = (admin: { uid: string; fullName: string }, settingName: string, oldValue: any, newValue: any): void => {
  addSystemLog({
    level: 'INFO',
    category: 'System Settings',
    action: 'Settings Changed',
    userId: admin.uid,
    userName: admin.fullName,
    resource: 'System Settings',
    resourceId: settingName,
    details: `System setting '${settingName}' changed from '${oldValue}' to '${newValue}' by ${admin.fullName}`,
    success: true,
    metadata: {
      settingName,
      oldValue,
      newValue,
      changedBy: admin.uid
    }
  });
};

// ===== ERROR LOGGING =====

// Log general errors
export const logError = (user: { uid: string; fullName: string }, errorMessage: string, context: string, metadata?: any): void => {
  addSystemLog({
    level: 'ERROR',
    category: 'Error',
    action: 'Error Occurred',
    userId: user.uid,
    userName: user.fullName,
    details: `Error in ${context}: ${errorMessage}`,
    success: false,
    errorCode: 'GENERAL_ERROR',
    metadata: {
      context,
      errorMessage,
      ...metadata
    }
  });
};

// ===== UI INTERACTION LOGGING =====

// Log button clicks for important actions
export const logButtonClick = (user: { uid: string; fullName: string }, buttonName: string, context: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'UI Interaction',
    action: 'Button Click',
    userId: user.uid,
    userName: user.fullName,
    details: `User ${user.fullName} clicked '${buttonName}' button in ${context}`,
    success: true,
    metadata: {
      buttonName,
      context
    }
  });
};

// Log page navigation
export const logPageNavigation = (user: { uid: string; fullName: string }, fromPage: string, toPage: string): void => {
  addSystemLog({
    level: 'INFO',
    category: 'Navigation',
    action: 'Page Navigation',
    userId: user.uid,
    userName: user.fullName,
    details: `User ${user.fullName} navigated from ${fromPage} to ${toPage}`,
    success: true,
    metadata: {
      fromPage,
      toPage
    }
  });
};
